import { useEffect, useRef, useState } from 'react';
import { useDialogStore } from '../../lib/dialog/store';
import { PillButton } from './PillButton';

export function DialogRenderer() {
  const open = useDialogStore((s) => s.open);
  const kind = useDialogStore((s) => s.kind);
  const options = useDialogStore((s) => s.options);
  const inputValue = useDialogStore((s) => s.inputValue);
  const setInputValue = useDialogStore((s) => s.setInputValue);
  const close = useDialogStore((s) => s.close);

  const inputRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      const id = window.requestAnimationFrame(() => setMounted(true));
      return () => window.cancelAnimationFrame(id);
    }
    setMounted(false);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (kind === 'prompt') {
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 80);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, kind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(null);
      } else if (e.key === 'Enter' && kind !== 'prompt') {
        e.preventDefault();
        close(true);
      } else if (e.key === 'Enter' && kind === 'prompt' && (e.target as HTMLElement)?.tagName !== 'BUTTON') {
        e.preventDefault();
        close(inputValue);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, kind, inputValue, close]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const tone = options.tone ?? 'default';
  const eyebrow = options.eyebrow ?? (kind === 'prompt' ? 'INPUT' : tone === 'danger' ? 'CONFIRM' : kind === 'confirm' ? 'CONFIRM' : 'NOTICE');
  const confirmLabel = options.confirmLabel ?? (kind === 'prompt' ? 'Save' : tone === 'danger' ? 'Delete' : 'OK');
  const cancelLabel = options.cancelLabel ?? 'Cancel';
  const confirmVariant = tone === 'danger' ? 'danger' : 'primary';

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close(null);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby={options.message ? 'dialog-message' : undefined}
      onClick={onBackdropClick}
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6 bg-cohere-black/40 backdrop-blur-sm transition-opacity duration-200 ${mounted ? 'opacity-100' : 'opacity-0'}`}
    >
      <div
        className={`w-full sm:max-w-md bg-canvas-white border border-hairline rounded-lg shadow-soft transition-all duration-200 ease-editorial ${mounted ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95'}`}
      >
        <div className="p-6 sm:p-8">
          <p className="mono-label text-muted-slate">{eyebrow}</p>
          <h2 id="dialog-title" className="mt-2 font-display text-card-heading text-ink">
            {options.title}
          </h2>
          {options.message && (
            <p id="dialog-message" className="mt-3 text-body text-muted-slate whitespace-pre-wrap">
              {options.message}
            </p>
          )}

          {kind === 'prompt' && (
            <div className="mt-5">
              {options.inputLabel && <label className="field-label">{options.inputLabel}</label>}
              <input
                ref={inputRef}
                type={options.inputType ?? 'text'}
                value={inputValue}
                placeholder={options.inputPlaceholder}
                onChange={(e) => setInputValue(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-hairline bg-canvas-white px-3 py-3 text-body text-ink focus:outline-none focus:ring-2 focus:ring-action-blue/40 focus:border-action-blue"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-6 sm:px-8 py-4 sm:py-5 border-t border-hairline">
          {(kind === 'confirm' || kind === 'prompt') && (
            <PillButton variant="ghost" onClick={() => close(null)} type="button">
              {cancelLabel}
            </PillButton>
          )}
          <PillButton
            variant={confirmVariant}
            onClick={() => {
              if (kind === 'prompt') {
                if (inputValue.trim() === '' && options.inputType !== 'number') {
                  inputRef.current?.focus();
                  return;
                }
                close(inputValue);
              } else if (kind === 'confirm') {
                close(true);
              } else {
                close(true);
              }
            }}
            type="button"
          >
            {confirmLabel}
          </PillButton>
        </div>
      </div>
    </div>
  );
}
